import React, { useMemo } from "react";
import { Focus } from "lucide-react";
import Tooltip from "../ui/Tooltip";
import { useSelectionStore } from "../../stores/selectionStore";
import { useBodies, useSites, useWorldBodies } from "../../stores/sceneStore";
import { getBodyTree } from "../../stores/sceneActions/bodyUtils";
import { findBodyForJointUuid } from "../../utils/bodyHierarchy";
import { autofocusCameraToSelection } from "../../selected-mesh-visibility";
import { getCameraViewAutofocusOptions } from "../../runtime/cameraViewPreferences";
import { INSPECTOR_CHROME_ICON_BUTTON_CLASS } from "../../utils/inspectorChromeButtonClasses";

const ChromeAutofocusButton = ({ tooltip, ariaLabel, onClick }) => (
  <div onMouseDown={(event) => event.stopPropagation()}>
    <Tooltip content={tooltip}>
      <button
        data-no-panel-drag="true"
        type="button"
        aria-label={ariaLabel}
        className={INSPECTOR_CHROME_ICON_BUTTON_CLASS}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <Focus className="w-3.5 h-3.5" aria-hidden />
      </button>
    </Tooltip>
  </div>
);

function useMeshInspectorParentBodyUuid(meshUuid) {
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  return useMemo(() => {
    if (!meshUuid) return null;
    const bodyTree = getBodyTree({ bodies, worldBodies });
    if (!Array.isArray(bodyTree)) return null;
    const findParent = (nodes) => {
      for (const node of nodes) {
        const hasMesh =
          Array.isArray(node?.geoms) && node.geoms.some((g) => g && g.mesh === meshUuid);
        if (hasMesh) return node.uuid;
        if (Array.isArray(node?.children) && node.children.length > 0) {
          const found = findParent(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findParent(bodyTree);
  }, [meshUuid, bodies, worldBodies]);
}

/**
 * Inspector title-row autofocus control (same framing as the visibility-section Focus buttons).
 * Intentionally avoids visibility hooks so we do not duplicate overlay/focus state with the panel toolbars.
 */
export const InspectorChromeAutofocusButton = React.memo(function InspectorChromeAutofocusButton({
  activePanel,
  resolvedSelectedGeom,
}) {
  const selectedBody = useSelectionStore((s) => s.selectedBody);
  const selectedMesh = useSelectionStore((s) => s.selectedMesh);
  const selectedJoint = useSelectionStore((s) => s.selectedJoint);
  const selectedSite = useSelectionStore((s) => s.selectedSite);
  const selectedInertial = useSelectionStore((s) => s.selectedInertial);
  const bodies = useBodies();
  const sites = useSites();

  const meshParentBodyUuid = useMeshInspectorParentBodyUuid(
    activePanel === "mesh" && selectedMesh ? selectedMesh : null
  );

  const jointBodyUuid = useMemo(() => {
    if (!selectedJoint || !bodies) return null;
    return findBodyForJointUuid(bodies, selectedJoint);
  }, [selectedJoint, bodies]);

  const resolvedSiteUuid = useMemo(() => {
    if (!selectedSite) return null;
    const direct = sites?.[selectedSite];
    if (direct?.uuid) return direct.uuid;
    const byName = Object.values(sites || {}).find((entry) => entry?.name === selectedSite);
    return byName?.uuid || null;
  }, [selectedSite, sites]);

  const visualGeomBodyUuid = resolvedSelectedGeom?.bodyUuid || null;
  const visualGeom = resolvedSelectedGeom?.geom || null;
  const visualGeomMeshUuid = useMemo(() => {
    if (!visualGeom || typeof visualGeom.mesh !== "string") return null;
    const trimmed = visualGeom.mesh.trim();
    return trimmed || null;
  }, [visualGeom]);

  const collisionBodyUuid = resolvedSelectedGeom?.bodyUuid || null;
  const collisionGeomUuid = resolvedSelectedGeom?.geom?.uuid
    ? String(resolvedSelectedGeom.geom.uuid)
    : null;

  const opts = getCameraViewAutofocusOptions();

  if ((activePanel === "body" || activePanel === "object") && selectedBody) {
    return (
      <ChromeAutofocusButton
        tooltip="Autofocus camera on selected body"
        ariaLabel="Autofocus camera on selected body"
        onClick={() => autofocusCameraToSelection({ bodyUuid: selectedBody, ...opts })}
      />
    );
  }

  if (activePanel === "visualGeom" && visualGeomBodyUuid && !visualGeomMeshUuid) {
    return (
      <ChromeAutofocusButton
        tooltip="Autofocus camera on selected visual geom"
        ariaLabel="Autofocus camera on selected visual geom"
        onClick={() => autofocusCameraToSelection({ bodyUuid: visualGeomBodyUuid, ...opts })}
      />
    );
  }

  if (activePanel === "mesh" && selectedMesh && meshParentBodyUuid) {
    return (
      <ChromeAutofocusButton
        tooltip="Autofocus camera on selected mesh"
        ariaLabel="Autofocus camera on selected mesh"
        onClick={() =>
          autofocusCameraToSelection({
            bodyUuid: meshParentBodyUuid,
            meshUuid: selectedMesh,
            ...opts,
          })
        }
      />
    );
  }

  if (activePanel === "visualGeom" && visualGeomBodyUuid && visualGeomMeshUuid) {
    return (
      <ChromeAutofocusButton
        tooltip="Autofocus camera on selected visual geom"
        ariaLabel="Autofocus camera on selected visual geom"
        onClick={() =>
          autofocusCameraToSelection({
            bodyUuid: visualGeomBodyUuid,
            meshUuid: visualGeomMeshUuid,
            ...opts,
          })
        }
      />
    );
  }

  if (activePanel === "joint" && selectedJoint && jointBodyUuid) {
    return (
      <ChromeAutofocusButton
        tooltip="Autofocus camera on selected joint"
        ariaLabel="Autofocus camera on selected joint"
        onClick={() =>
          autofocusCameraToSelection({
            bodyUuid: jointBodyUuid,
            jointUuid: selectedJoint,
            ...opts,
          })
        }
      />
    );
  }

  if (activePanel === "geom" && collisionBodyUuid && collisionGeomUuid) {
    return (
      <ChromeAutofocusButton
        tooltip="Autofocus camera on selected collision geom"
        ariaLabel="Autofocus camera on selected collision geom"
        onClick={() =>
          autofocusCameraToSelection({
            bodyUuid: collisionBodyUuid,
            collisionGeomUuid: collisionGeomUuid,
            ...opts,
          })
        }
      />
    );
  }

  if (activePanel === "site" && resolvedSiteUuid) {
    return (
      <ChromeAutofocusButton
        tooltip="Autofocus camera on selected site"
        ariaLabel="Autofocus camera on selected site"
        onClick={() => autofocusCameraToSelection({ siteUuid: resolvedSiteUuid, ...opts })}
      />
    );
  }

  if (activePanel === "inertial" && selectedInertial) {
    const key = String(selectedInertial);
    return (
      <ChromeAutofocusButton
        tooltip="Autofocus camera on selected inertial"
        ariaLabel="Autofocus camera on selected inertial"
        onClick={() => autofocusCameraToSelection({ inertialUuid: key, ...opts })}
      />
    );
  }

  return null;
});
